import { useRef, useState } from 'react';
import { Info, Loader2, Shapes } from 'lucide-react';
import { CallMenu, CallModal, type Choice } from './CallBadge';
import { TIP_BADGE } from './HoverTip';
import { TIP_LABEL, tipSection } from './ReasoningBadge';
import { cropForTrace } from './partCrop';
import { listingImageSrc, listingTracePrompt, type ListingCrop } from './listingCropsRun';
import { RUN_MODEL, modelLabel } from './models';
import { useLanguage } from '../../contexts/LanguageContext';

// THE SECOND PASS, READ BACK: one call per box, each tracing one part in its own close crop.
//
// SO THE MENU IS NOT THE TWO HALVES OF ONE CALL. Every other badge in this app offers
// "Prompt" and "Reasoning & output", which is a real choice when there is one request and one
// reply to split. Here a photograph with six rectangles on it was six requests, in six frames,
// answered six times — and a single "prompt" panel over that would have to pick one of them
// and pass it off as the question, or show six pictures over one text and claim a call nobody
// made.
//
// ONE ROW PER PART, AND ITS INSTANCES TOGETHER IN THE WINDOW. A wood button found twice was
// traced twice, in two different crops, and the two answers can disagree — but they were sent
// the SAME words: the prompt names the part, and nothing in it knows which of the two this is.
// So the question is printed once and the crops are stacked under it, which is the shape of
// what actually happened. Two menu rows reading "Wood button" would have said the opposite.
type Box = { crop: ListingCrop; part: number; name: string };

// A LINE PER POLYGON, in the reply as it came. How many shapes a trace sent is the first
// thing anybody asks of this panel — a rear frame is three or four, a seat is one — and in
// one wrapped paragraph of six hundred numbers it is the one thing that cannot be seen. The
// break at each ring is the whole answer: count the lines.
//
// THE REPLY IS NOT REWRITTEN, only laid out. Whatever the model wrapped it in — a fence, a
// sentence — is kept exactly, and a reply that will not parse is printed untouched: this
// panel is where a bad answer is read, and a formatter that tidies one has hidden it.
const isNum = (v: unknown): v is number => typeof v === 'number';
const isPair = (v: unknown) => Array.isArray(v) && v.length === 2 && v.every(isNum);

export const byPolygon = (raw: string): string => {
  const text = (raw || '').trim();
  const open = text.indexOf('{');
  const close = text.lastIndexOf('}');
  if (open < 0 || close <= open) return text;
  let outline: unknown;
  try {
    outline = (JSON.parse(text.slice(open, close + 1)) as { outline?: unknown })?.outline;
  } catch {
    return text;
  }
  // THE NAMED SPELLING FIRST, which is what the prompt asks for: one entry per polygon, and
  // the entry printed with it — the key is the model's own count of its shapes and belongs on
  // the line it names.
  if (outline && !Array.isArray(outline) && typeof outline === 'object') {
    const named = Object.entries(outline as Record<string, unknown>);
    if (!named.length) return text;
    return `${text.slice(0, open)}{"outline": {\n${
      named.map(([k, v]) => `${JSON.stringify(k)}: ${JSON.stringify(v)}`).join(',\n')
    }\n}}${text.slice(close + 1)}`;
  }
  if (!Array.isArray(outline) || !outline.length) return text;
  // And the four list spellings a model still sends (see _ring_groups): a flat list of numbers
  // and a list of [y, x] pairs are each ONE ring — no ring is two numbers — and anything else
  // is one entry per ring.
  const rings = outline.every(isNum) || outline.every(isPair)
    ? [outline]
    : outline.filter((v) => Array.isArray(v));
  return `${text.slice(0, open)}{"outline": [\n${
    rings.map((ring) => JSON.stringify(ring)).join(',\n')
  }\n]}${text.slice(close + 1)}`;
};

// One part's calls on this photograph: the words they all carried, and a crop each.
type Group = { part: number; name: string; crops: ListingCrop[] };

// What one group's calls were made of, once fetched: the shared question, and each
// instance's PICTURES in the order the zoom draws them — the crop, and the same crop with the
// first pass's rectangle on it where one was sent. Both, because this panel's whole job is
// showing what the model was shown, and the second picture is half of what it was asked.
type Sent = { prompt: string; images: (Cut | null)[] };
type Cut = { image: string; hint: string | null };

export function MasksBadge({ boxes }: { boxes: Box[] }) {
  const { t } = useLanguage();
  const badge = useRef<HTMLButtonElement>(null);
  // Where the menu hangs, or null; and which part is open, or null. Two states rather than
  // one, as in CallBadge: picking a row closes the menu and opens the window, and a single
  // value would have to describe both halves of that moment.
  const [menuOpen, setMenuOpen] = useState(false);
  const [showing, setShowing] = useState<number | null>(null);
  // Kept by position: a part read once opens again with no round trip.
  const [sent, setSent] = useState<Record<number, Sent>>({});

  if (!boxes.length) return null;

  // BY PART, in the order their first box is drawn — so the menu is read in the order the
  // labels sit on the picture behind it. By index and not by name: the index is the part's
  // identity, and two rows of one manual are allowed to print the same name.
  const groups: Group[] = [];
  for (const { crop, part, name } of boxes) {
    const had = groups.find((g) => g.part === part);
    if (had) had.crops.push(crop);
    else groups.push({ part, name, crops: [crop] });
  }

  const label = (g: Group) => (g.crops.length > 1 ? `${g.name} (${g.crops.length})` : g.name);

  const fetchOne = (i: number) => {
    if (sent[i]) return;
    const g = groups[i];
    void Promise.all([
      // ONE PROMPT FOR THE GROUP. Every instance's call was built from the same line — the
      // part's name — so fetching it per crop would be the same string asked for three
      // times.
      // Hinted if any of this part's boxes carried one: the words are the group's, and a
      // group is only ever mixed while a run traced before the raw boxes were kept.
      listingTracePrompt(g.name, g.crops.some((c) => !!c.rawBbox))
        .catch(() => ''),
      Promise.all(g.crops.map(async (c) => {
        const cut = await cropForTrace(listingImageSrc(c.url), c.rawBbox ?? c.bbox);
        return cut && { image: cut.image, hint: c.rawBbox ? cut.hint : null };
      })),
    ]).then(([prompt, images]) => setSent((was) => ({ ...was, [i]: { prompt, images } })));
  };

  const choices: Choice[] = groups.map((g, i) => ({
    key: String(i),
    icon: <Shapes className="h-3.5 w-3.5" />,
    label: label(g),
    panel: null,
  }));

  const press = () => {
    if (menuOpen) return setMenuOpen(false);
    // A photograph with one part traced on it has nothing to choose between, so the badge
    // opens it — the same answer CallBadge gives when its menu is a menu of one.
    if (groups.length === 1) {
      fetchOne(0);
      return setShowing(0);
    }
    setMenuOpen(true);
  };

  const open = showing == null ? null : sent[showing];
  const group = showing == null ? null : groups[showing];

  return (
    <>
      <button
        ref={badge}
        type="button"
        onClick={press}
        title={t('notice.parts.masksTitle')}
        aria-label={t('notice.parts.masksTitle')}
        className={TIP_BADGE}
      >
        <Info className="h-3.5 w-3.5" />
      </button>

      {menuOpen && (
        <CallMenu
          anchor={badge}
          choices={choices}
          onClose={() => setMenuOpen(false)}
          onPick={(key) => {
            const i = Number(key);
            setMenuOpen(false);
            fetchOne(i);
            setShowing(i);
          }}
        />
      )}

      {group && (
        <CallModal title={label(group)} onClose={() => setShowing(null)}>
          {/* HOW MANY CALLS THIS PART WAS, said before the first crop. It is the fact the
              panel exists to carry: the shapes on the photograph came back one at a time. */}
          <p className={`mb-3 text-center ${TIP_LABEL}`}>
            {(modelLabel(RUN_MODEL) ? t('notice.parts.masksCount') : t('notice.parts.masksCountBare'))
              .replace('{n}', String(group.crops.length))
              .replace('{model}', modelLabel(RUN_MODEL))}
          </p>
          <p className="mb-3 text-center text-xs font-semibold text-slate-700">
            {label(group)}
          </p>
          {!open ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* THE QUESTION, ONCE, above the crops it was asked of. */}
              {tipSection(t('notice.tip.menuPrompt'), open.prompt || '—')}
              {group.crops.map((crop, n) => (
                <div
                  key={n}
                  className="space-y-2 border-t border-slate-700 pt-4"
                >
                  {group.crops.length > 1 && (
                    <p className={`text-center ${TIP_LABEL}`}>
                      {t('notice.parts.masksNth')
                        .replace('{n}', String(n + 1))
                        .replace('{of}', String(group.crops.length))}
                    </p>
                  )}
                  {/* WHAT THE MODEL WAS ACTUALLY SHOWN. The whole argument for this pass is
                      the frame: the part fills it and nothing else is in it to be confused
                      with, which is a claim you can only check by looking at the crop. */}
                  {open.images[n] && (
                    <div className="flex flex-wrap items-start justify-center gap-2">
                      {[open.images[n]?.image, open.images[n]?.hint]
                        .filter((src): src is string => !!src)
                        .map((src) => (
                          <img
                            key={src.slice(-32)}
                            src={src}
                            alt=""
                            className="max-h-56 rounded-lg border border-slate-700 bg-white object-contain"
                          />
                        ))}
                    </div>
                  )}
                  {/* THE ANSWER, OR WHY THERE ISN'T ONE. A shape drawn before this panel
                      existed was traced by a call whose reply was read for its polygons and
                      then dropped, and two empty sections read as a call that said nothing —
                      which is a different thing, and the interesting one. */}
                  {crop.note ? (
                    <>
                      {tipSection(
                        t('notice.cat.reasoningTitle'),
                        crop.note.reasoning || t('notice.cat.noReasoning'),
                      )}
                      {tipSection(t('notice.cat.rawOutputTitle'), byPolygon(crop.note.raw) || '—')}
                    </>
                  ) : (
                    <p className="text-center text-xs italic text-slate-400">
                      {t('notice.parts.masksNoNote')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CallModal>
      )}
    </>
  );
}
